import { jsPDF } from "jspdf";
import 'jspdf-autotable';
import notify from "@/utils/notify";
import {decrypt} from "@/utils/crypto";

/**
 * Export data to PDF
 * @param {Object} options - Configuration options for the PDF
 * @param {string} options.orientation - Orientation of the PDF (default: "landscape")
 * @param {string} options.topTitle - Top title of the PDF
 * @param {string} options.title - Title of the PDF
 * @param {Array} options.info - Array of information lines to display at the top of the PDF
 * @param {string} options.belowOrgTitle - Title below the organization name
 * @param {Array} options.belowOrgInfo - Array of information lines below the organization name
 * @param {Array} options.tableColumns - Array of column headers for the table
 * @param {Array} options.tableData - Array of data rows for the table
 * @param {Object|Array} options.totalRow - Object or array containing total row data (optional)
 * @param {string} options.totalRow.label - Label for the total row (e.g., "TOTAL:")
 * @param {string|number} options.totalRow.value - Value for the total row
 * @param {number} options.totalRow.colSpan - Number of columns to span before the total label
 * @param {string} options.filename - Filename for the PDF
 * @param {Function} options.formatNumber - Function to format numbers (optional)
 * @returns {void}
 */
export default function exportToPDF(options) {
  try {
    let organization = JSON.parse(decrypt(sessionStorage.getItem('organization')));
    let profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    let store = JSON.parse(decrypt(sessionStorage.getItem('store')));
    const {
      orientation = "landscape",
      topTitle = "",
      title,
      info = [],
      belowOrgTitle = "",
      belowOrgInfo = [],
      tableColumns,
      tableData,
      totalRow,
      filename,
      formatNumber = (val) => val.toString()
    } = options;

    // Create a new PDF document
    const doc = new jsPDF({orientation: orientation});
    let docWidth = doc.internal.pageSize.getWidth();
    let titleChars = title.length;
    let centerOffset = (docWidth / 2) + titleChars + (titleChars / 1.3);

    // Add top title
    let yPos = 20;
    if (topTitle !== "") {
      doc.setFontSize(20);
      doc.text(topTitle, doc.internal.pageSize.width - centerOffset, yPos);
      yPos += 10;
    }

    // Add title
    doc.setFontSize(15);
    doc.text(organization.name, 14, yPos);
    doc.text(title, doc.internal.pageSize.width - 15, yPos, {
      align: 'right',
    });

    // Add information
    doc.setFontSize(10);
    yPos = yPos + 5;

    doc.text(store.phone, 14, yPos);
    doc.text(organization.email, 14, yPos + 5);
    doc.text(store.name + ' - ' + store.address, 14, yPos + 10);

    info.forEach(line => {
      doc.text(line, doc.internal.pageSize.width - 15, yPos, {
        align: 'right',
      });
      yPos += 5;
    });

    if (belowOrgTitle !== "" && belowOrgInfo.length > 0) {
      doc.setFontSize(15);
      doc.text(belowOrgTitle, 14, yPos + 5);

      doc.setFontSize(10);
      belowOrgInfo.forEach(line => {
        doc.text(line, 14, yPos + 10);
        yPos += 5;
      });
      yPos += 10;
    }

    // Prepare table rows
    const tableRows = [...tableData];

    // Add total row(s) if provided
    if (totalRow) {
      if (Array.isArray(totalRow)) {
        // Handle array of total rows
        totalRow.forEach(row => {
          const emptyColumns = Array(row.colSpan || 0).fill("");
          const totalRowData = [
            ...emptyColumns,
            row.label,
            formatNumber(row.value)
          ];
          tableRows.push(totalRowData);
        });
      } else {
        // Handle single total row object (backward compatibility)
        const emptyColumns = Array(totalRow.colSpan || 0).fill("");
        const totalRowData = [
          ...emptyColumns,
          totalRow.label,
          formatNumber(totalRow.value)
        ];
        tableRows.push(totalRowData);
      }
    }

    // Generate the table
    if (tableRows.length > 0) {
      doc.autoTable({
        head: [tableColumns],
        body: tableRows,
        startY: yPos + 5,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 3,
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold'
        },
        footStyles: {
          fillColor: [240, 240, 240],
          textColor: 0,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        // Add custom styling for total rows
        didParseCell: function(data) {
          // Check if this is a total row by looking for the label (like "TOTAL:")
          if (data.row.raw &&
              data.row.raw.some(cell => typeof cell === 'string' && cell.includes('TOTAL:'))) {
              data.cell.styles.fontSize = 10;
              data.cell.styles.textColor = [41, 128, 185];
              data.cell.styles.fontStyle = 'bold';
          }
        }
      });
    } else {
      doc.text('No data to display', 14, yPos + 5);
    }

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    let dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
      doc.text(
          `Generated on: ${new Date().toLocaleString("en-US", dateOptions)} by ${profile.first_name} ${profile.last_name}`,
          14,
          doc.internal.pageSize.height - 10
      );
    }

    // Save the PDF
    doc.save(filename);

    // Show success notification
    notify.alert.success('PDF exported successfully', 3000);
  } catch (error) {
    console.error('Error exporting PDF:', error);
    notify.alert.error('Error exporting PDF', 3000);
  }
}
